// frontend/src/hooks/useRestDayStreak.js
import { useQuery } from '@tanstack/react-query';
import { staffAPI } from '../services/api';

/**
 * Self-scoped MOM Employment Act s36 rest-day streak awareness.
 *
 * Surfaces the applicant's current consecutive-working-day streak, and —
 * when `proposedStart` / `proposedEnd` are supplied — the PROJECTED streak
 * assuming that date range is approved as a work-counting leave type
 * (WFH / External Activity both count as "worked" days for MOM s36, per
 * `RestDayComplianceService.WORK_LEAVE_TYPES`).
 *
 * Intentionally read-only / advisory: the caller decides whether to render
 * a callout from `has_warning` — this hook never blocks submission.
 *
 * @param {Object} params
 * @param {string} [params.proposedStart] - YYYY-MM-DD start date being drafted.
 * @param {string} [params.proposedEnd] - YYYY-MM-DD end date being drafted.
 * @param {boolean} [params.enabled] - Gate the query (e.g. only for WFH / External Activity).
 */
export const useRestDayStreak = ({ proposedStart, proposedEnd, enabled = true } = {}) => {
  const params = {};
  if (proposedStart) params.proposed_start = proposedStart;
  if (proposedEnd) params.proposed_end = proposedEnd;

  return useQuery({
    queryKey: ['attendance', 'restDayStreak', params],
    queryFn: async () => {
      const response = await staffAPI.getMyRestDayStreak(params);
      return response.data || { has_warning: false };
    },
    enabled,
    staleTime: 60 * 1000, // 1 minute - streak can change as attendance normalizes
  });
};

export default useRestDayStreak;
